/**
 * venuesRoutes — the Venues surface's route table (BON-538 phase 2).
 *
 * Defined here rather than inline in `AdminRoutes.tsx` so the routes and their
 * permission gate live in this feature folder, following `giftCardsRoutes`.
 * Paths are relative — admin mode's tree mounts them under `/admin/*`.
 *
 * One page for every account holding the grant, with the scope in the query
 * string. The detail page keeps its own tab segment and is addressed by record
 * id — see `roster/venue-routes.ts` for why not by slug.
 */
import { Route } from 'react-router-dom';

import RequirePermission from '../../../shared/RequirePermission';
import { Permission } from '../../../types/permissions';
import { DashboardsPage } from '../DashboardsPage';

import { VenuesPage } from './roster/VenuesPage';
import VenueDetailPage from './VenueDetailPage';

// `write:venues` opens the page as surely as `read:venues` does: a Regional
// Director holds the write grant and not the read one, so gating on read alone
// locked the one non-Admin role that manages regional venues out of the page it
// manages them from, while the API happily accepted their writes (BON-568).
//
// Widening the gate rather than granting RD `read:venues` keeps this a code
// change: no tenant deploy, nothing half-applied if that deploy fails, and no
// window where an RD holds a token minted before the grant. It reaches exactly
// the same people — Admin and Regional Director are the only holders of either
// venue grant.
const gate = (element: React.ReactNode) => (
  <RequirePermission anyOf={[Permission.READ_VENUES, Permission.WRITE_VENUES, Permission.READ_ALL]}>
    {element}
  </RequirePermission>
);

export const venuesRoutes = (
  <>
    <Route path="venues" element={gate(<VenuesPage />)} />
    {/* The detail page carries no chrome of its own — it has to be given a
        `.nb-dashboards` scope, or every token var resolves to nothing and the
        page renders unstyled. */}
    <Route
      path="venues/:venueId/:tab?"
      element={gate(
        <DashboardsPage stack={false}>
          <VenueDetailPage />
        </DashboardsPage>,
      )}
    />
  </>
);
